import { useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { SpotLoading } from '@enterprise/spot';
import { observer } from 'mobx-react';
import { Route } from 'react-router';
import { ItemEditPage } from '../ItemEditPage';
import { ItemSearchPanel } from './itemsSearchPanel';
import { GetItemQuery, InvoiceItem } from '../../../../core/models';
import { ItemsGrid, ItemsGridColumn, ItemsType } from '../itemsGrid';
import { ItemsGroupByType } from '../../../../core/models/organization/settings/enum';
import { useStores } from '../../../../hooks';
import { ItemEditQuery } from '../../../../store/invoiceItem.store';
import { BulkUpdateModal } from '../modals/BulkUpdateModal';
import styles from './invoiceItems.component.module.scss';
import i18n from 'i18next';
import { ItemAddPractices } from '../../components/ItemAddPractice/ItemAddPractices';
import { useItemBulkAction } from './useItemBulkAction.hook';
import { BulkUpdateFlyoverType } from '../ItemsPageStore';
import { RootRoutes } from '../../../../router';
import { RouterPaths } from '../../../../router';
import { isEqual } from 'lodash';

const DEFAULT_PAGE_SIZE = 25;

export const InvoiceItems = observer(function InvoiceItemsComponent() {
    const {
        domain: { itemsPageStore, constantsStore, invoiceItemStore },
    } = useStores();

    const oldSearchQuery = useRef<GetItemQuery>();
    const { addToPractices, bulkUpdate } = useItemBulkAction();
    const { itemsGroupBy, isLoadingItems: isLoadingItemsLegacy } = itemsPageStore;
    const { isLoadingItems, items = [], selectionModel, pagingModel, updateSelectionModel, itemsGridState } = invoiceItemStore;
    const { isLoading: isConstantsLoading } = constantsStore;
    const isLoading = (!items.length && isLoadingItems) || isLoadingItemsLegacy || isConstantsLoading;
    const { pageSize = DEFAULT_PAGE_SIZE, page = 0, searchQuery } = itemsGridState || {};
    const grouping: ItemsGridColumn =
        (itemsGroupBy === ItemsGroupByType.Description && ItemsGridColumn.Description) ||
        (itemsGroupBy === ItemsGroupByType.Id && ItemsGridColumn.Id) ||
        ItemsGridColumn.Id;

    useEffect(() => {
        return () => {
            invoiceItemStore.clearItemsState();
        };
    }, []);

    useEffect(() => {
        if (isLoading) {
            void itemsPageStore.load();
        }
    }, [isLoading]);

    useEffect(() => {
        (async () => {
            if (!searchQuery) {
                return;
            }

            await invoiceItemStore.fetchItems({ ...searchQuery, page, size: pageSize });

            if (!isEqual(oldSearchQuery.current, searchQuery)) {
                updateSelectionModel(undefined);
                oldSearchQuery.current = searchQuery;
            }
        })();
    }, [page, pageSize, searchQuery]);

    const searchItems = async (query: GetItemQuery): Promise<void> => {
        invoiceItemStore.updateGridState({ ...itemsGridState, page: 0, pageSize, searchQuery: query });
    };

    const onEditHandler = (params: { query: ItemEditQuery }) => {
        const query: ItemEditQuery = {
            ...params.query,
            id: grouping === ItemsGridColumn.Id ? params.query.id : undefined,
            description: grouping === ItemsGridColumn.Description ? params.query.description : undefined,
        };

        invoiceItemStore.openItemEditForm({ query });
    };

    const searchItemsForAutoComplete = async (query: GetItemQuery): Promise<InvoiceItem[]> => {
        return await invoiceItemStore.fetchItemsForAutoComplete(query);
    };

    const onBulkActionChange = (props: { type: BulkUpdateFlyoverType }): void => {
        const { type } = props;

        if (type === BulkUpdateFlyoverType.AddToPractices) {
            addToPractices.action();
        }

        if (
            type === BulkUpdateFlyoverType.Description ||
            type === BulkUpdateFlyoverType.Classification ||
            type === BulkUpdateFlyoverType.Status ||
            type === BulkUpdateFlyoverType.Price ||
            type === BulkUpdateFlyoverType.SupplierName ||
            type === BulkUpdateFlyoverType.SupplierCode ||
            type === BulkUpdateFlyoverType.AnalysisGroup1 ||
            type === BulkUpdateFlyoverType.AnalysisGroup2 ||
            type === BulkUpdateFlyoverType.AnalysisGroup3
        ) {
            bulkUpdate.action(type);
        }
    };

    const onDownloadPreview = async () => {
        if (!selectionModel) {
            return;
        }

        const includedItems = [...(selectionModel.items?.values() || [])].map(({ pimsGeneratedId }) => pimsGeneratedId);
        const excludedItems = [...(selectionModel.excluded?.values() || [])].map(({ pimsGeneratedId }) => pimsGeneratedId);

        await invoiceItemStore.downloadPreviewCSV({
            itemsSearchQuery: searchQuery,
            includedItems: selectionModel.all ? undefined : includedItems,
            excludedItems: selectionModel.all ? excludedItems : undefined,
        });
    };

    return (
        <div className={styles.holder} data-automation-id="items-search-results">
            <RootRoutes>
                <Route path={`${RouterPaths.ControlCenterPages.Items}/:action`} element={<ItemEditPage />} />
            </RootRoutes>

            <Container className={styles.container} fluid={true}>
                <ItemSearchPanel searchItems={searchItems} searchItemsForAutoComplete={searchItemsForAutoComplete} />

                {isLoading && <SpotLoading />}
                {Boolean(items?.length) && (
                    <ItemsGrid
                        className={styles.grid}
                        type={(itemsPageStore.isAnimanaOnly && ItemsType.Animana) || undefined}
                        invoiceItems={items}
                        loading={isLoadingItems}
                        grouping={[grouping]}
                        onEdit={onEditHandler}
                        selection={selectionModel}
                        pagination={pagingModel}
                        onSelectionChange={updateSelectionModel}
                        onDownloadPreview={onDownloadPreview}
                        onBulkActionChange={onBulkActionChange}
                        gridState={itemsGridState}
                        onGridStateChange={(state) => {
                            invoiceItemStore.updateGridState(state);
                        }}
                    />
                )}
            </Container>

            {addToPractices.itemsKeys && (
                <ItemAddPractices
                    sitesWithoutItems={addToPractices.practicesWithoutItems}
                    title={i18n.t('controlCenter:addItemToAdditionalPractices', 'Add this item to Additional Practices', {
                        count: addToPractices.itemsKeys.size,
                    })}
                    secondaryTitle={i18n.t('controlCenter:itemsSelected', `[${addToPractices.itemsKeys.size}] Item(s) Selected`, {
                        count: addToPractices.itemsKeys.size,
                    })}
                    onSubmit={async (params) => {
                        await addToPractices.submit(params);
                        updateSelectionModel(undefined);
                    }}
                    onClose={addToPractices.cancel}
                />
            )}

            {bulkUpdate.type && (
                <BulkUpdateModal
                    type={bulkUpdate.type}
                    selectedItems={bulkUpdate.selectedItems}
                    invoiceItems={bulkUpdate.invoiceItems}
                    excludedItems={[...(selectionModel?.excluded?.values() || [])]}
                    searchQuery={searchQuery}
                    selectionModel={selectionModel}
                    onClose={({ reason }) => {
                        bulkUpdate.cancel();
                        if (reason === 'action') {
                            updateSelectionModel(undefined);
                        }
                    }}
                />
            )}
        </div>
    );
});
